/**
 * Comprehensive scoring configuration system for teacher dashboard integration
 */

export type PointMultiplier = 0 | 1 | 2 | 3; // Zero, Normal, Double, Triple points

export interface ScoringConfig {
  // Base scoring parameters (teacher configurable)
  basePoints: number; // Default: 100 points per correct answer
  enableSpeedBonus: boolean; // Teacher can toggle speed bonus on/off
  speedBonusMaxPercent: number; // Maximum speed bonus as percentage of base (max 10%)

  // Question-specific multipliers (set by teacher per question)
  pointMultipliers: {
    zero: 0;    // No points questions
    normal: 1;  // Standard points
    double: 2;  // Double points questions
    triple: 3;  // Triple points questions
  };

  // Advanced scoring features (future teacher options)
  enableStreakBonus: boolean; // Bonus for consecutive correct answers
  streakBonusPoints: number;  // Points per streak level
  penalizeIncorrect: boolean; // Whether to deduct points for wrong answers
  incorrectPenalty: number;   // Points deducted for wrong answers
}

export interface RoundScore {
  roundNumber: number;
  questionId: string;

  // Scoring breakdown
  basePoints: number;        // Base points for correct/incorrect
  speedBonus: number;        // Bonus for being faster than other players
  streakBonus: number;       // Bonus for answer streak (future feature)
  multiplierBonus: number;   // Points from question multiplier (2x, 3x, etc.)
  penalty: number;           // Points deducted (if enabled)

  // Final calculation
  finalScore: number;        // Total points earned this round

  // Performance data
  responseTime: number;      // Time taken to answer (milliseconds)
  isCorrect: boolean;        // Whether the answer was correct
  selectedAnswer: number;    // Which answer was selected (0-3)

  // Competitive context
  playerRank: number;        // Speed ranking among correct answers (1 = fastest)
  totalCorrectAnswers: number; // How many players got it right
}

export interface PlayerScoreHistory {
  playerId: string;
  roundScores: RoundScore[]; // Detailed breakdown per round
  totalScore: number;        // Cumulative score across all rounds

  // Performance stats
  correctAnswers: number;
  totalAnswers: number;
  averageResponseTime: number;
  fastestResponse: number;
  currentStreak: number;     // Current consecutive correct answers
  longestStreak: number;     // Best streak in this game
}

export interface SpeedBonusCalculation {
  playerTime: number;
  fastestCorrectTime: number;
  medianCorrectTime: number;
  playersFasterCount: number;
  totalCorrectPlayers: number;
  bonusEligible: boolean;    // Whether player qualifies for speed bonus
  bonusAmount: number;       // Calculated bonus points
}

export interface ScoringSession {
  sessionId: string;
  config: ScoringConfig;
  playerHistories: Map<string, PlayerScoreHistory>;
  currentRound: number;

  // Real-time data for speed calculations
  currentRoundResponses: Map<string, {
    responseTime: number;
    isCorrect: boolean;
    selectedAnswer: number;
  }>;
}

export interface QuestionScoringData {
  questionId: string;
  pointMultiplier: PointMultiplier; // Teacher-set multiplier for this question
  correctAnswerIndex: number;

  // Response data from all players
  responses: Array<{
    playerId: string;
    selectedAnswer: number;
    responseTime: number;
    isCorrect: boolean;
  }>;
}

// Default configuration that teachers can override
export const DEFAULT_SCORING_CONFIG: ScoringConfig = {
  basePoints: 100,
  enableSpeedBonus: true,
  speedBonusMaxPercent: 10, // Max 10% bonus as requested
  pointMultipliers: {
    zero: 0,
    normal: 1,
    double: 2,
    triple: 3,
  },
  enableStreakBonus: false,
  streakBonusPoints: 5,
  penalizeIncorrect: false,
  incorrectPenalty: 10,
};

// Validation constants
export const SCORING_LIMITS = {
  MAX_BASE_POINTS: 1000,
  MIN_BASE_POINTS: 1,
  MAX_SPEED_BONUS_PERCENT: 10, // Hard limit: 10% max
  MIN_SPEED_BONUS_PERCENT: 0,
  MAX_STREAK_BONUS: 50,
  MAX_INCORRECT_PENALTY: 50,
} as const;